import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { auditAPI, dashboardAPI, intelligenceAPI } from '../lib/api';
import { Brain, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const VenueIntelligencePage = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [savingTrigger, setSavingTrigger] = useState(false);
  const [error, setError] = useState('');
  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [triggers, setTriggers] = useState([]);
  const [events, setEvents] = useState([]);
  const [actions, setActions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [networkInsights, setNetworkInsights] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [triggerStatusFilter, setTriggerStatusFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('30d');
  const [triggerForm, setTriggerForm] = useState({
    trigger_type: 'audit_failure',
    action_type: 'notify_manager',
  });

  const eventTypeOptions = useMemo(() => {
    const types = [...new Set(events.map((event) => event.event_type).filter(Boolean))];
    return ['all', ...types];
  }, [events]);

  const filteredTriggers = useMemo(() => {
    if (triggerStatusFilter === 'all') {
      return triggers;
    }
    const expectedActive = triggerStatusFilter === 'active';
    return triggers.filter((trigger) => Boolean(trigger.active) === expectedActive);
  }, [triggers, triggerStatusFilter]);

  const cutoffDate = useMemo(() => {
    if (dateRangeFilter === 'all') {
      return null;
    }
    const now = new Date();
    const days = Number(dateRangeFilter.replace('d', ''));
    if (Number.isNaN(days)) {
      return null;
    }
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - days);
    return cutoff;
  }, [dateRangeFilter]);

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (eventTypeFilter !== 'all' && event.event_type !== eventTypeFilter) {
        return false;
      }
      if (!cutoffDate) {
        return true;
      }
      const eventDate = new Date(event.timestamp);
      return eventDate >= cutoffDate;
    });
  }, [events, eventTypeFilter, cutoffDate]);

  const filteredActions = useMemo(() => {
    if (!cutoffDate) {
      return actions;
    }
    return actions.filter((action) => {
      const actionDate = new Date(action.created_at);
      return actionDate >= cutoffDate;
    });
  }, [actions, cutoffDate]);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  };

  const fetchPageData = useCallback(async (venueIdOverride = null) => {
    setLoading(true);
    setError('');
    try {
      const statsRes = await dashboardAPI.getStats();
      const venueList = statsRes?.data?.venues || [];
      setVenues(venueList);

      const targetVenueId = venueIdOverride || selectedVenueId || venueList[0]?.id || '';
      if (!selectedVenueId && targetVenueId) {
        setSelectedVenueId(targetVenueId);
      }

      const allVenueIdsCsv = venueList.map((venue) => venue.id).filter(Boolean).join(',');

      const [scorecardsRes, networkRes, suggestionsRes, triggerRes, eventRes, actionRes, timelineRes] = await Promise.all([
        intelligenceAPI.getManagerScorecards(allVenueIdsCsv).catch(() => ({ data: { scorecards: [] } })),
        intelligenceAPI.getNetworkInsights(allVenueIdsCsv).catch(() => ({ data: null })),
        intelligenceAPI.getSuggestions().catch(() => ({ data: { suggestions: [] } })),
        targetVenueId ? intelligenceAPI.listTriggers(targetVenueId).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        targetVenueId ? intelligenceAPI.getVenueEvents(targetVenueId, null, 50).catch(() => ({ data: { events: [] } })) : Promise.resolve({ data: { events: [] } }),
        targetVenueId ? intelligenceAPI.getVenueActionLog(targetVenueId, 50).catch(() => ({ data: { actions: [] } })) : Promise.resolve({ data: { actions: [] } }),
        targetVenueId ? intelligenceAPI.getVenueTimeline(targetVenueId, 200).catch(() => ({ data: { timeline: [] } })) : Promise.resolve({ data: { timeline: [] } }),
      ]);

      setScorecards(scorecardsRes?.data?.scorecards || []);
      setNetworkInsights(networkRes?.data || null);
      setSuggestions(suggestionsRes?.data?.suggestions || []);
      setTriggers(triggerRes?.data || []);
      setEvents(eventRes?.data?.events || []);
      setActions(actionRes?.data?.actions || []);
      setTimeline(timelineRes?.data?.timeline || []);
    } catch (fetchError) {
      console.error('Venue intelligence load failed:', fetchError);
      setError('Failed to load venue intelligence data.');
    } finally {
      setLoading(false);
    }
  }, [selectedVenueId]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const onVenueChange = async (venueId) => {
    setSelectedVenueId(venueId);
    await fetchPageData(venueId);
  };

  const createTrigger = async () => {
    if (!selectedVenueId) return;
    setSavingTrigger(true);
    setError('');
    try {
      await intelligenceAPI.createTrigger({
        venue_id: selectedVenueId,
        trigger_type: triggerForm.trigger_type,
        condition: {},
        action: {
          type: triggerForm.action_type,
          title: `Auto: ${triggerForm.trigger_type}`,
          message: `Automated ${triggerForm.action_type} for ${triggerForm.trigger_type}`,
        },
        active: true,
      });
      const triggerRes = await intelligenceAPI.listTriggers(selectedVenueId);
      setTriggers(triggerRes?.data || []);
    } catch (triggerError) {
      console.error('Create trigger failed:', triggerError);
      setError(triggerError?.response?.data?.detail || 'Could not create trigger.');
    } finally {
      setSavingTrigger(false);
    }
  };

  const toggleTrigger = async (trigger) => {
    try {
      await intelligenceAPI.updateTrigger(trigger.trigger_id, { active: !trigger.active });
      setTriggers((prev) => prev.map((item) => (
        item.trigger_id === trigger.trigger_id ? { ...item, active: !item.active } : item
      )));
    } catch (toggleError) {
      console.error('Toggle trigger failed:', toggleError);
      setError(toggleError?.response?.data?.detail || 'Could not update trigger.');
    }
  };

  const deleteTrigger = async (triggerId) => {
    try {
      await intelligenceAPI.deleteTrigger(triggerId);
      setTriggers((prev) => prev.filter((item) => item.trigger_id !== triggerId));
    } catch (deleteError) {
      console.error('Delete trigger failed:', deleteError);
      setError(deleteError?.response?.data?.detail || 'Could not delete trigger.');
    }
  };

  const resetFilters = () => {
    setEventTypeFilter('all');
    setTriggerStatusFilter('all');
    setDateRangeFilter('30d');
  };

  const loadAuditReport = async (auditId) => {
    try {
      const response = await auditAPI.getIntelligenceReport(auditId);
      setSelectedReport(response?.data || null);
      toast.success('Audit report loaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load audit report');
    }
  };

  const downloadAuditReportPdf = async (auditId) => {
    try {
      const response = await auditAPI.getIntelligenceReportPdfBinary(auditId);
      const filename = `audit_intelligence_${auditId}.pdf`;

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { FilePicker } = await import('@capawesome/capacitor-file-picker');
        const base64Data = arrayBufferToBase64(response.data);

        const tempPath = `audit_exports/${filename}`;
        await Filesystem.writeFile({
          path: tempPath,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        });
        const tempUri = await Filesystem.getUri({ path: tempPath, directory: Directory.Documents });

        try {
          const pickedDirectory = await FilePicker.pickDirectory();
          await FilePicker.copyFile({ from: tempUri.uri, to: `${pickedDirectory.path}/${filename}`, overwrite: true });
          toast.success('Audit PDF saved to selected folder');
        } catch (pickError) {
          await Filesystem.writeFile({
            path: `Download/${filename}`,
            data: base64Data,
            directory: Directory.ExternalStorage,
            recursive: true,
          });
          toast.success('Folder selection cancelled. PDF saved to Downloads');
          console.warn('Directory picker failed, fallback to Downloads:', pickError);
        }

        try {
          await Filesystem.deleteFile({ path: tempPath, directory: Directory.Documents });
        } catch (cleanupError) {
          console.warn('Temporary PDF cleanup failed:', cleanupError);
        }
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
      toast.success('Audit PDF downloaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download audit PDF');
    }
  };

  if (!hasMinimumRole(user, ROLES.MANAGER)) {
    return (
      <div className="card-rtb text-center py-12">
        <h2 className="font-heading text-xl font-bold text-white uppercase mb-2">Access Restricted</h2>
        <p className="text-[#A1A1AA]">You need Manager role or above to view venue intelligence.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="venue-intelligence-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-2">
            <Brain className="w-8 h-8 text-[#FF6600]" />
            Venue Intelligence
          </h1>
          <p className="text-[#A1A1AA] mt-1">Operational control panel for triggers, events, actions, and recommendations.</p>
        </div>
      </div>

      {error && <div className="card-rtb border border-[#3F3F46] text-[#FFB300] text-sm">{error}</div>}

      <div className="card-rtb space-y-3">
        <label className="text-sm text-[#A1A1AA]">Venue</label>
        <select
          value={selectedVenueId}
          onChange={(event) => onVenueChange(event.target.value)}
          className="w-full md:w-96 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
        >
          {venues.map((venue) => (
            <option key={venue.id} value={venue.id}>{venue.name}</option>
          ))}
        </select>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 pt-1">
          <select
            value={eventTypeFilter}
            onChange={(event) => setEventTypeFilter(event.target.value)}
            className="bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
          >
            {eventTypeOptions.map((eventType) => (
              <option key={eventType} value={eventType}>{eventType}</option>
            ))}
          </select>
          <select
            value={triggerStatusFilter}
            onChange={(event) => setTriggerStatusFilter(event.target.value)}
            className="bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
          >
            <option value="all">all trigger status</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
          <select
            value={dateRangeFilter}
            onChange={(event) => setDateRangeFilter(event.target.value)}
            className="bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
          >
            <option value="7d">last 7 days</option>
            <option value="30d">last 30 days</option>
            <option value="90d">last 90 days</option>
            <option value="all">all time</option>
          </select>
          <Button variant="secondary" className="btn-rtb-secondary" onClick={resetFilters}>
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card-rtb xl:col-span-2">
          <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Manager Scorecards</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#A1A1AA] border-b border-[#3F3F46]">
                  <th className="py-2 pr-3">Venue</th>
                  <th className="py-2 pr-3">Compliance</th>
                  <th className="py-2 pr-3">Tasks Completed</th>
                  <th className="py-2 pr-3">Audit Pass Rate</th>
                  <th className="py-2 pr-3">Open Issues</th>
                </tr>
              </thead>
              <tbody>
                {scorecards.map((card) => (
                  <tr key={card.venue_id} className="border-b border-[#27272A]">
                    <td className="py-2 pr-3 text-white">{card.venue_name}</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">{card.compliance_score}%</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">{card.task_completion_rate}%</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">{card.audit_pass_rate}%</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">{card.open_issue_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-rtb">
          <h2 className="font-heading text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF6600]" />
            Suggestions
          </h2>
          <div className="space-y-3">
            {suggestions.map((item, index) => (
              <div key={`${item.title}-${index}`} className="border border-[#3F3F46] rounded-sm p-3">
                <p className="text-white text-sm font-medium">{item.title}</p>
                <p className="text-[#A1A1AA] text-xs mt-1">{item.detail}</p>
                <p className="text-[#FFB300] text-xs mt-1">{item.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {networkInsights && (
        <div className="card-rtb">
          <h2 className="font-heading text-lg font-bold text-white uppercase mb-2">Network Insight</h2>
          <p className="text-[#A1A1AA] text-sm">
            Recurring Audit Failures: {networkInsights.recurring_audit_failures || 0} ·
            SLA Breaches: {networkInsights.high_sla_breaches || 0} ·
            Checklist Misses: {networkInsights.frequent_checklist_misses || 0}
          </p>
          <p className="text-[#FFB300] text-sm mt-1">{networkInsights.recommendation}</p>
        </div>
      )}

      <div className="card-rtb space-y-3">
        <h2 className="font-heading text-lg font-bold text-white uppercase">Manager Action Timeline</h2>
        <div className="space-y-2 max-h-64 overflow-auto">
          {timeline.map((item, idx) => (
            <div key={`${item.type}-${item.timestamp}-${idx}`} className="border border-[#3F3F46] rounded-sm p-3">
              <p className="text-white text-sm font-medium">{item.title || item.type}</p>
              <p className="text-[#A1A1AA] text-xs">{item.type} · {new Date(item.timestamp).toLocaleString()}</p>
            </div>
          ))}
          {!timeline.length && <p className="text-[#A1A1AA] text-sm">No timeline entries found.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card-rtb space-y-3">
          <h2 className="font-heading text-lg font-bold text-white uppercase">Automation Triggers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={triggerForm.trigger_type}
              onChange={(event) => setTriggerForm((prev) => ({ ...prev, trigger_type: event.target.value }))}
              className="bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
            >
              <option value="audit_failure">audit_failure</option>
              <option value="task_overdue">task_overdue</option>
              <option value="checklist_missed">checklist_missed</option>
              <option value="sunset">sunset</option>
              <option value="football_match">football_match</option>
              <option value="weather_alert">weather_alert</option>
              <option value="sales_drop">sales_drop</option>
            </select>
            <select
              value={triggerForm.action_type}
              onChange={(event) => setTriggerForm((prev) => ({ ...prev, action_type: event.target.value }))}
              className="bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
            >
              <option value="notify_manager">notify_manager</option>
              <option value="send_alert">send_alert</option>
              <option value="create_task">create_task</option>
              <option value="suggest_improvement">suggest_improvement</option>
            </select>
            <Button className="btn-rtb-primary" disabled={savingTrigger || !selectedVenueId} onClick={createTrigger}>
              {savingTrigger ? 'Saving...' : 'Create Trigger'}
            </Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {filteredTriggers.map((trigger) => (
              <div key={trigger.trigger_id} className="flex items-center justify-between gap-3 border border-[#3F3F46] rounded-sm p-3">
                <div>
                  <p className="text-white text-sm">{trigger.trigger_type}</p>
                  <p className="text-[#A1A1AA] text-xs">{trigger.active ? 'active' : 'inactive'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" className="btn-rtb-secondary h-8 px-3 text-xs" onClick={() => toggleTrigger(trigger)}>
                    {trigger.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="secondary" className="btn-rtb-secondary h-8 px-2" onClick={() => deleteTrigger(trigger.trigger_id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
            {!filteredTriggers.length && <p className="text-[#A1A1AA] text-sm">No triggers match current filters.</p>}
          </div>
        </div>

        <div className="card-rtb space-y-4">
          <div>
            <h2 className="font-heading text-lg font-bold text-white uppercase mb-2">Recent Events</h2>
            <div className="space-y-2 max-h-52 overflow-auto">
              {filteredEvents.map((event) => (
                <div key={event.event_id} className="border border-[#3F3F46] rounded-sm p-2">
                  <p className="text-white text-xs font-medium">{event.event_type}</p>
                  <p className="text-[#A1A1AA] text-[11px]">{new Date(event.timestamp).toLocaleString()}</p>
                  {event.event_type === 'audit_completed' && event.payload?.audit_id && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button variant="secondary" className="btn-rtb-secondary h-7 px-2 text-xs" onClick={() => loadAuditReport(event.payload.audit_id)}>
                        View Report
                      </Button>
                      <Button variant="secondary" className="btn-rtb-secondary h-7 px-2 text-xs" onClick={() => downloadAuditReportPdf(event.payload.audit_id)}>
                        Download PDF
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {!filteredEvents.length && <p className="text-[#A1A1AA] text-sm">No events match current filters.</p>}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-lg font-bold text-white uppercase mb-2">Recent Action Log</h2>
            <div className="space-y-2 max-h-52 overflow-auto">
              {filteredActions.map((action) => (
                <div key={action.action_id} className="border border-[#3F3F46] rounded-sm p-2">
                  <p className="text-white text-xs font-medium">{action.action_type}</p>
                  <p className="text-[#A1A1AA] text-[11px]">{action.title}</p>
                  <p className="text-[#A1A1AA] text-[11px]">{new Date(action.created_at).toLocaleString()}</p>
                </div>
              ))}
              {!filteredActions.length && <p className="text-[#A1A1AA] text-sm">No action logs match current filters.</p>}
            </div>
          </div>
        </div>
      </div>

      {selectedReport && (
        <div className="card-rtb space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-white uppercase">Audit Report Preview</h2>
            <Button variant="secondary" className="btn-rtb-secondary" onClick={() => setSelectedReport(null)}>Close</Button>
          </div>
          <div className="bg-[#111111] border border-[#3F3F46] rounded-sm p-3 text-xs text-[#A1A1AA] overflow-auto max-h-72">
            <pre>{JSON.stringify(selectedReport?.analysis || selectedReport, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueIntelligencePage;
